import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { X, ArrowSquareOut, FloppyDisk } from "@phosphor-icons/react";

export const ContactQuickView = ({ contactId, onClose, onSaved }) => {
  const [c, setC] = useState(null);
  const [tags, setTags] = useState([]);
  const [savedTags, setSavedTags] = useState([]);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("active");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getContact(contactId).then(d => {
      setC(d);
      setTags(d.tags || []);
      setNotes(d.notes || "");
      setStatus(d.status || "active");
    }).catch(e => { toast.error(fmtErr(e)); onClose(); });
    api.listTags().then(d => setSavedTags((d.items || d || []).map(t => t.name || t))).catch(() => {});
  }, [contactId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateContact(contactId, { tags, notes, status });
      toast.success("Contact updated");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(false); }
  };

  const toggleTag = (t) => setTags(tags.includes(t) ? tags.filter(x => x !== t) : [...tags, t]);

  return createPortal(
    <div className="fixed inset-0 z-50" data-testid="contact-quickview">
      <div className="absolute inset-0 bg-zinc-950/30" onClick={onClose} data-testid="quickview-backdrop"/>
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl border-l border-zinc-200 flex flex-col">
        {!c ? <div className="p-6 text-sm text-zinc-400">Loading…</div> : (
          <>
            <div className="p-5 border-b border-zinc-100 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-heading text-lg font-medium truncate">{c.first_name} {c.last_name}</h3>
                <div className="text-xs text-zinc-500 truncate">{[c.job_title, c.company].filter(Boolean).join(" · ") || "—"}</div>
                <div className="text-xs font-mono text-zinc-600 mt-1 truncate">{c.email}</div>
              </div>
              <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 shrink-0" data-testid="quickview-close"><X size={18}/></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md border border-zinc-100 bg-zinc-50 py-2">
                  <div className="text-sm font-semibold">{c.engagement_score ?? 0}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Engagement</div>
                </div>
                <div className="rounded-md border border-zinc-100 bg-zinc-50 py-2">
                  <div className="text-sm font-semibold">{c.total_opens ?? 0}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Opens</div>
                </div>
                <div className="rounded-md border border-zinc-100 bg-zinc-50 py-2">
                  <div className="text-sm font-semibold">{c.total_clicks ?? 0}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Clicks</div>
                </div>
              </div>

              <label className="block">
                <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Status</span>
                <select data-testid="quickview-status" value={status} onChange={e => setStatus(e.target.value)}
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm">
                  <option value="active">Active</option>
                  <option value="unsubscribed">Unsubscribed</option>
                  <option value="bounced">Bounced</option>
                  <option value="left_company">Left company</option>
                </select>
                {status === "left_company" && <p className="text-[11px] text-orange-600 mt-1">Excluded from every campaign and sequence until set back to active.</p>}
              </label>

              <div>
                <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Tags</span>
                <input data-testid="quickview-tags" value={tags.join(", ")}
                  onChange={e => setTags(e.target.value.split(",").map(t => t.trim()).filter(Boolean))}
                  placeholder="comma, separated, tags"
                  className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm"/>
                {savedTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {savedTags.slice(0, 12).map(t => (
                      <button key={t} onClick={() => toggleTag(t)} data-testid={`quickview-tag-${t}`}
                        className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${tags.includes(t) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <label className="block">
                <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Notes</span>
                <textarea data-testid="quickview-notes" rows={5} value={notes} onChange={e => setNotes(e.target.value)}
                  className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm"/>
              </label>
            </div>

            <div className="p-4 border-t border-zinc-100 flex items-center justify-between gap-2">
              <Link to={`/contacts/${contactId}`} className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline" data-testid="quickview-full-profile">
                <ArrowSquareOut size={13}/> Open full profile
              </Link>
              <button data-testid="quickview-save" onClick={save} disabled={saving}
                className="inline-flex items-center gap-1.5 text-sm px-4 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50">
                <FloppyDisk size={14}/> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};
